<script setup lang="ts">
  // Native printer edit modal. Network printers: rename/re-address through
  // the atomic updateNetworkPrinter mutator (re-points live refs + persisted
  // names), then re-probe on address change (warn-but-save, matching add
  // semantics). USB/BT: the device-reported name IS the assignment key —
  // identity is read-only, only assignments are editable. Ghosts never get
  // here (card offers "Quitar asignaciones" instead).
  import { computed, getCurrentInstance, nextTick, ref } from 'vue';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { IPrinter } from '@/interfaces/IPrinter';
  import {
    INativeDestinationSelection,
    parseHostPort,
    useNativePrintingUi,
  } from '@/composition/printing/use-native-printing-ui';
  import { useNativePrintTests } from '@/composition/printing/use-native-print-tests';
  import { IPersistedPrinterSettings } from '@/composition/use-printing';
  import { CODEPAGE_ITEMS } from '@/lib/print/codepages';
  import {
    IEscposPrinterSettings,
    IZplPrinterSettings,
    PrinterCutType,
    PrinterModel,
    QrRenderMode,
    ZplDpmm,
    normalizeEscposSettings,
    normalizeZplSettings,
  } from '@/lib/print/printer-settings';
  import { PrinterCutMode } from '@/stores/printing-v2.config';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiListbox from '@/components/pos-v2/ui/PosUiListbox.vue';
  import V2NativeAssignmentPicker from './V2NativeAssignmentPicker.vue';

  const props = defineProps<{
    /** Assignment identity (card name) of the printer being edited. */
    printerName: string;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const ui = useUi();
  const nativeUi = useNativePrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  // Snapshot at open: the edit works on a copy and commits on Save.
  const card =
    nativeUi.printerCards.value.find((c) => c.name === props.printerName) ??
    null;
  const isNetwork = !!card?.networkConfig;

  const name = ref(card?.name ?? '');
  const host = ref(card?.networkConfig?.host ?? '');
  const port = ref(String(card?.networkConfig?.port ?? 9100));

  function selectionOf(printerName: string): INativeDestinationSelection[] {
    return (nativeUi.assignmentsByName.value.get(printerName) ?? []).map(
      (a) => ({ destination: a.destination, stationId: a.stationId })
    );
  }

  const originalSelection = selectionOf(props.printerName);
  const selection = ref<INativeDestinationSelection[]>([...originalSelection]);
  const saving = ref(false);

  // #region hardware settings (per-printer map, keyed by name)

  const tests = useNativePrintTests();

  /** Live IPrinter shim used by the in-modal test buttons. */
  const testTarget = computed<IPrinter | null>(() => {
    if (card?.printer) return card.printer;
    if (isNetwork && card?.networkConfig) {
      const parsed = parseHostPort(host.value, port.value);
      if (parsed.ok) {
        return {
          type: 'tcp',
          id: `${parsed.host}:${parsed.port}`,
          name: props.printerName,
        };
      }
    }
    return null;
  });

  const storedEntry: IPersistedPrinterSettings | undefined =
    nativeUi.store.printerSettings[props.printerName];

  /**
   * Printer format: the persisted `type` wins (wizard/migration seeded);
   * a printer holding the sticker assignment without an entry is a label
   * printer by construction.
   */
  const printerType: 'escpos' | 'zpl' =
    storedEntry?.type ??
    (nativeUi.assignedNameOf('sticker') === props.printerName
      ? 'zpl'
      : 'escpos');

  // Seed both forms from the resolver (normalized, with legacy fallbacks).
  const seeded = nativeUi.store.printerSettingsOf(
    card?.printer ??
      (card?.networkConfig
        ? {
            type: 'tcp',
            id: `${card.networkConfig.host}:${card.networkConfig.port}`,
            name: props.printerName,
          }
        : null)
  );

  // ESC/POS fields.
  const paperWidthMm = ref<58 | 80>(seeded.escpos.paperWidthMm);
  const codepage = ref(seeded.escpos.codepage);
  const model = ref<PrinterModel>(seeded.escpos.model);
  const qrMode = ref<QrRenderMode>(seeded.escpos.qrMode);
  const cutType = ref<PrinterCutType>(seeded.escpos.cutType);
  const cutMode = ref<PrinterCutMode>(seeded.escpos.cutMode);
  // Empty = per-document legacy feed (byte parity for untouched printers).
  const feedLines = ref(
    seeded.escpos.feedLines != null ? String(seeded.escpos.feedLines) : ''
  );
  const charsNormal = ref(
    seeded.escpos.charsPerLineNormal != null
      ? String(seeded.escpos.charsPerLineNormal)
      : ''
  );
  const charsSmall = ref(
    seeded.escpos.charsPerLineSmall != null
      ? String(seeded.escpos.charsPerLineSmall)
      : ''
  );
  const advancedOpen = ref(
    seeded.escpos.charsPerLineNormal != null ||
      seeded.escpos.charsPerLineSmall != null
  );

  // ZPL fields.
  const dpmm = ref<ZplDpmm>(seeded.zpl.dpmm);
  const zplWidthMm = ref(String(seeded.zpl.widthMm));
  const zplHeightMm = ref(String(seeded.zpl.heightMm));
  const padTop = ref(String(seeded.zpl.padding.top));
  const padRight = ref(String(seeded.zpl.padding.right));
  const padBottom = ref(String(seeded.zpl.padding.bottom));
  const padLeft = ref(String(seeded.zpl.padding.left));
  const zplFontSize = ref<IZplPrinterSettings['fontSize']>(seeded.zpl.fontSize);
  const offsetXmm = ref(String(seeded.zpl.offsetXmm));
  const offsetYmm = ref(String(seeded.zpl.offsetYmm));

  function toIntOrNull(raw: string): number | null {
    const value = Number(raw.trim());
    return raw.trim() !== '' && Number.isFinite(value) ? value : null;
  }

  function toNumberOr(raw: string, fallback: number): number {
    const value = Number(raw.trim());
    return raw.trim() !== '' && Number.isFinite(value) ? value : fallback;
  }

  function formEscpos(): IEscposPrinterSettings {
    return normalizeEscposSettings({
      model: model.value,
      codepage: codepage.value,
      paperWidthMm: paperWidthMm.value,
      qrMode: qrMode.value,
      charsPerLineNormal: toIntOrNull(charsNormal.value),
      charsPerLineSmall: toIntOrNull(charsSmall.value),
      cutType: cutType.value,
      cutMode: cutMode.value,
      feedLines: toIntOrNull(feedLines.value),
    });
  }

  function formZpl(): IZplPrinterSettings {
    return normalizeZplSettings({
      dpmm: dpmm.value,
      widthMm: toNumberOr(zplWidthMm.value, seeded.zpl.widthMm),
      heightMm: toNumberOr(zplHeightMm.value, seeded.zpl.heightMm),
      padding: {
        top: toNumberOr(padTop.value, seeded.zpl.padding.top),
        right: toNumberOr(padRight.value, seeded.zpl.padding.right),
        bottom: toNumberOr(padBottom.value, seeded.zpl.padding.bottom),
        left: toNumberOr(padLeft.value, seeded.zpl.padding.left),
      },
      fontSize: zplFontSize.value,
      offsetXmm: toNumberOr(offsetXmm.value, 0),
      offsetYmm: toNumberOr(offsetYmm.value, 0),
    });
  }

  /** Narrows a listbox emit to a whitelisted union member (else fallback). */
  function pickOption<T extends string | number>(
    value: number | string | null,
    allowed: readonly T[],
    fallback: T
  ): T {
    return (allowed as readonly (string | number)[]).includes(
      value as string | number
    )
      ? (value as T)
      : fallback;
  }

  const MODEL_OPTIONS: readonly PrinterModel[] = [
    'generic',
    'genericb',
    'epsona',
  ];
  const QR_MODE_OPTIONS: readonly QrRenderMode[] = ['auto', 'native', 'image'];
  const CUT_TYPE_OPTIONS: readonly PrinterCutType[] = ['partial', 'full'];
  const CUT_MODE_OPTIONS: readonly PrinterCutMode[] = ['modern', 'legacy'];
  const DPMM_OPTIONS: readonly ZplDpmm[] = [6, 8, 12, 24];
  const FONT_SIZE_OPTIONS: readonly IZplPrinterSettings['fontSize'][] = [
    'xs',
    's',
    'm',
    'l',
  ];

  const codepageItems = CODEPAGE_ITEMS;

  const modelItems = computed(() => [
    { id: 'generic', label: i18nT('printingV2.modelGeneric') },
    { id: 'genericb', label: i18nT('printingV2.modelGenericB') },
    { id: 'epsona', label: i18nT('printingV2.modelEpsonA') },
  ]);

  const qrModeItems = computed(() => [
    { id: 'auto', label: i18nT('printingV2.qrModeAuto') },
    { id: 'native', label: i18nT('printingV2.qrModeNative') },
    { id: 'image', label: i18nT('printingV2.qrModeImage') },
  ]);

  const qrModeHelp = computed(() => {
    if (qrMode.value === 'native') return i18nT('printingV2.qrModeNativeHelp');
    if (qrMode.value === 'image') return i18nT('printingV2.qrModeImageHelp');
    return i18nT('printingV2.qrModeAutoHelp');
  });

  const cutTypeItems = computed(() => [
    { id: 'partial', label: i18nT('printingV2.cutTypePartial') },
    { id: 'full', label: i18nT('printingV2.cutTypeFull') },
  ]);

  const cutModeItems = computed(() => [
    { id: 'modern', label: i18nT('printingV2.cutModeModern') },
    { id: 'legacy', label: i18nT('printingV2.cutModeLegacy') },
  ]);

  const dpmmItems = [6, 8, 12, 24].map((value) => ({
    id: value,
    label: `${value} dpmm`,
  }));

  const fontSizeItems = computed(() => [
    { id: 'xs', label: i18nT('printingV2.zplFontXs') },
    { id: 's', label: i18nT('printingV2.zplFontS') },
    { id: 'm', label: i18nT('printingV2.zplFontM') },
    { id: 'l', label: i18nT('printingV2.zplFontL') },
  ]);

  const zplWidthDots = computed(() =>
    Math.round(toNumberOr(zplWidthMm.value, 0) * dpmm.value)
  );
  const zplHeightDots = computed(() =>
    Math.round(toNumberOr(zplHeightMm.value, 0) * dpmm.value)
  );

  const testingHardware = computed(() => tests.testingPrinterId.value !== null);

  function testCodepages() {
    if (!testTarget.value) return;
    void tests.testEncodings(testTarget.value, formEscpos());
  }

  function testQr() {
    if (!testTarget.value) return;
    void tests.testQr(testTarget.value, formEscpos());
  }

  function testZplCalibration() {
    if (!testTarget.value) return;
    void tests.testZplCalibration(testTarget.value, formZpl());
  }

  // #endregion

  const nameError = computed<string | null>(() => {
    if (!isNetwork) return null;
    const trimmed = name.value.trim();
    if (!trimmed) return i18nT('printingV2.nativeNameRequired');
    if (
      trimmed !== props.printerName &&
      nativeUi.takenPrinterNames.value.has(trimmed)
    ) {
      return i18nT('printingV2.nativeNameTaken');
    }
    return null;
  });

  const addressError = computed<string | null>(() => {
    if (!isNetwork) return null;
    const parsed = parseHostPort(host.value, port.value);
    if (parsed.ok) return null;
    return parsed.error === 'host'
      ? i18nT('printingV2.nativeInvalidHost')
      : i18nT('printingV2.nativeInvalidPort');
  });

  const addressChanged = computed(() => {
    if (!isNetwork || !card?.networkConfig) return false;
    const parsed = parseHostPort(host.value, port.value);
    if (!parsed.ok) return false;
    return (
      parsed.host !== card.networkConfig.host ||
      parsed.port !== card.networkConfig.port
    );
  });

  const canSave = computed(
    () => !!card && !saving.value && !nameError.value && !addressError.value
  );

  function sameSelection(
    a: INativeDestinationSelection,
    b: INativeDestinationSelection
  ): boolean {
    return a.destination === b.destination && a.stationId === b.stationId;
  }

  async function save() {
    if (!canSave.value || !card) return;
    saving.value = true;
    // Renderer-safety: don't mutate local reactive state after emit('close')
    // starts the modal teardown — only reset the spinner when staying open.
    let closed = false;
    try {
      let finalPrinter: IPrinter;

      if (isNetwork && card.networkConfig) {
        const parsed = parseHostPort(host.value, port.value);
        if (!parsed.ok) return;
        const trimmedName = name.value.trim();
        try {
          nativeUi.store.updateNetworkPrinter(props.printerName, {
            name: trimmedName,
            host: parsed.host,
            port: parsed.port,
          });
        } catch (e) {
          ui.showToast(
            AlertType.Error,
            e instanceof Error ? e.message : i18nT('printingV2.actionFailed')
          );
          return;
        }
        nativeUi.renameProbeState(props.printerName, trimmedName);
        finalPrinter = {
          type: 'tcp',
          id: `${parsed.host}:${parsed.port}`,
          name: trimmedName,
        };
      } else if (card.printer) {
        finalPrinter = card.printer;
      } else {
        return; // ghost — never editable
      }

      // Selection diff. updateNetworkPrinter already re-pointed the printer's
      // existing assignments to the new name, so the diff runs on the ORIGINAL
      // selection with the FINAL printer identity.
      for (const entry of selection.value) {
        if (originalSelection.some((o) => sameSelection(o, entry))) continue;
        if (entry.destination === 'ticket') {
          nativeUi.store.setTicketPrinter(finalPrinter);
        } else if (entry.destination === 'sticker') {
          nativeUi.store.setStickerPrinter(finalPrinter);
        } else if (entry.stationId !== undefined) {
          nativeUi.store.setComandaPrinter(entry.stationId, finalPrinter);
        }
      }
      for (const entry of originalSelection) {
        if (selection.value.some((s) => sameSelection(s, entry))) continue;
        // Guard: only clear when this printer still holds the destination —
        // never wipe an assignment another card took over meanwhile.
        const holder = nativeUi.assignedNameOf(
          entry.destination,
          entry.stationId
        );
        if (holder !== finalPrinter.name) continue;
        // clearAssignment (NOT set*Printer(null)): persist()'s name retention
        // would resurrect a plain null on the next discovery pass.
        nativeUi.store.clearAssignment(entry.destination, entry.stationId);
      }

      // Persist hardware settings under the FINAL name (updateNetworkPrinter
      // already re-keyed the map on rename). Preserving the other format's
      // stored partial keeps data across a type flip-flop.
      const existingEntry = nativeUi.store.printerSettings[finalPrinter.name];
      nativeUi.store.setPrinterSettings(finalPrinter.name, {
        type: printerType,
        escpos:
          printerType === 'escpos' ? formEscpos() : existingEntry?.escpos,
        zpl: printerType === 'zpl' ? formZpl() : existingEntry?.zpl,
      });

      // Re-probe the new address inside the saving spinner (warn-but-save:
      // the printer may simply be off — matching the add flow's semantics).
      if (isNetwork && addressChanged.value) {
        const updated = nativeUi.networkPrinters.value.find(
          (np) => np.name === finalPrinter.name
        );
        if (updated) {
          const reachable = await nativeUi.probeOne(updated);
          if (!reachable) {
            ui.showToast(AlertType.Info, i18nT('printingV2.nativeUnreachable'));
          }
        }
      }

      ui.showToast(AlertType.Success, i18nT('printingV2.saved'));
      // Let the save's re-renders flush BEFORE starting the modal's leave
      // transition: sharing a flush with the write burst can trip Vue's
      // circular-update breaker and strand the modal half-closed.
      await nextTick();
      closed = true;
      emit('close');
    } finally {
      if (!closed) saving.value = false;
    }
  }
</script>

<template>
  <div class="v2-native-printer-edit-modal">
    <div class="edit-header">
      <h3 class="edit-title">{{ $t('printingV2.nativeEditTitle') }}</h3>
      <!-- Closing mid-save would resume save()'s continuation against a
           torn-down modal (toast + no-op close on a dead instance). -->
      <button
        class="edit-close"
        type="button"
        :disabled="saving"
        @click="!saving && emit('close')"
      >
        <PosUiIcon name="close" :size="20" />
      </button>
    </div>

    <div v-if="card" class="edit-body">
      <template v-if="isNetwork">
        <div class="edit-field">
          <label class="edit-label">{{ $t('printingV2.fieldName') }}</label>
          <PosUiInput
            :value="name"
            :maxlength="40"
            @input="name = $event"
          />
          <div v-if="nameError" class="edit-error">{{ nameError }}</div>
        </div>

        <div class="edit-address-row">
          <div class="edit-field edit-field-host">
            <label class="edit-label">{{
              $t('printingV2.nativeEditHost')
            }}</label>
            <PosUiInput
              :value="host"
              placeholder="192.168.1.100"
              @input="host = $event"
            />
          </div>
          <div class="edit-field edit-field-port">
            <label class="edit-label">{{
              $t('printingV2.nativeEditPort')
            }}</label>
            <PosUiInput
              :value="port"
              inputmode="numeric"
              :maxlength="5"
              @input="port = $event"
            />
          </div>
        </div>
        <div v-if="addressError" class="edit-error">{{ addressError }}</div>
        <div v-if="addressChanged" class="edit-note">
          <PosUiIcon name="lan-pending" class="edit-note-icon" />
          {{ $t('printingV2.nativeEditReprobe') }}
        </div>
      </template>

      <template v-else>
        <div class="edit-identity">
          <div class="edit-identity-icon">
            <PosUiIcon
              :name="card.transport === 'usb' ? 'usb' : 'bluetooth'"
              :size="22"
            />
          </div>
          <div class="edit-identity-info">
            <div class="edit-identity-name">{{ card.name }}</div>
            <div class="edit-identity-detail">
              {{
                $t(
                  card.transport === 'usb'
                    ? 'printingV2.transportUsb'
                    : 'printingV2.transportBluetooth'
                )
              }}
              · {{ card.printer?.id }}
            </div>
          </div>
        </div>
        <div class="edit-note">
          <PosUiIcon name="lock-outline" class="edit-note-icon" />
          {{ $t('printingV2.nativeNameLockedNote') }}
        </div>
      </template>

      <!-- ESC/POS hardware section -->
      <template v-if="printerType === 'escpos'">
        <div class="edit-section-title">
          {{ $t('printingV2.sectionHardware') }}
        </div>

        <div class="edit-field">
          <label class="edit-label">{{ $t('printingV2.fieldPaper') }}</label>
          <div class="edit-paper-toggle">
            <button
              type="button"
              class="paper-option"
              :class="{ active: paperWidthMm === 80 }"
              @click="paperWidthMm = 80"
            >
              {{ $t('printingV2.paper80') }}
            </button>
            <button
              type="button"
              class="paper-option"
              :class="{ active: paperWidthMm === 58 }"
              @click="paperWidthMm = 58"
            >
              {{ $t('printingV2.paper58') }}
            </button>
          </div>
        </div>

        <div class="edit-field">
          <label class="edit-label">{{ $t('printingV2.fieldModel') }}</label>
          <PosUiListbox
            :value="model"
            :items="modelItems"
            @input="model = pickOption($event, MODEL_OPTIONS, 'generic')"
          />
        </div>

        <div class="edit-field">
          <label class="edit-label">{{ $t('printingV2.fieldCodepage') }}</label>
          <PosUiListbox
            :value="codepage"
            :items="codepageItems"
            searchable
            @input="codepage = String($event ?? 'cp858')"
          />
          <div class="edit-test-row">
            <PosUiButton
              size="sm"
              color="neutral"
              variant="outline"
              icon="text-search"
              :loading="testingHardware"
              :disabled="!testTarget || saving"
              @click="testCodepages"
            >
              {{ $t('printingV2.testCodepages') }}
            </PosUiButton>
            <PosUiButton
              size="sm"
              color="neutral"
              variant="outline"
              icon="qrcode"
              :loading="testingHardware"
              :disabled="!testTarget || saving"
              @click="testQr"
            >
              {{ $t('printingV2.testQr') }}
            </PosUiButton>
          </div>
        </div>

        <div class="edit-field">
          <label class="edit-label">{{ $t('printingV2.fieldQrMode') }}</label>
          <PosUiListbox
            :value="qrMode"
            :items="qrModeItems"
            @input="qrMode = pickOption($event, QR_MODE_OPTIONS, 'auto')"
          />
          <p class="edit-hint">{{ qrModeHelp }}</p>
        </div>

        <div class="edit-field-row">
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldCutType')
            }}</label>
            <PosUiListbox
              :value="cutType"
              :items="cutTypeItems"
              @input="cutType = pickOption($event, CUT_TYPE_OPTIONS, 'partial')"
            />
          </div>
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldCutMode')
            }}</label>
            <PosUiListbox
              :value="cutMode"
              :items="cutModeItems"
              @input="cutMode = pickOption($event, CUT_MODE_OPTIONS, 'modern')"
            />
          </div>
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldFeedLines')
            }}</label>
            <PosUiInput
              :value="feedLines"
              type="number"
              inputmode="numeric"
              :placeholder="$t('printingV2.charsAuto')"
              @input="feedLines = $event"
            />
          </div>
        </div>

        <!-- Collapsible advanced calibration -->
        <button
          type="button"
          class="edit-collapse-toggle"
          @click="advancedOpen = !advancedOpen"
        >
          <PosUiIcon
            :name="advancedOpen ? 'chevron-down' : 'chevron-right'"
            :size="18"
          />
          {{ $t('printingV2.sectionAdvancedCalibration') }}
        </button>
        <div v-if="advancedOpen" class="edit-advanced">
          <div class="edit-field-row">
            <div class="edit-field">
              <label class="edit-label">{{
                $t('printingV2.fieldCharsNormal')
              }}</label>
              <PosUiInput
                :value="charsNormal"
                type="number"
                inputmode="numeric"
                :placeholder="$t('printingV2.charsAuto')"
                clearable
                @input="charsNormal = $event"
              />
              <p class="edit-hint">{{ $t('printingV2.charsNormalHint') }}</p>
            </div>
            <div class="edit-field">
              <label class="edit-label">{{
                $t('printingV2.fieldCharsSmall')
              }}</label>
              <PosUiInput
                :value="charsSmall"
                type="number"
                inputmode="numeric"
                :placeholder="$t('printingV2.charsAuto')"
                clearable
                @input="charsSmall = $event"
              />
              <p class="edit-hint">{{ $t('printingV2.charsSmallHint') }}</p>
            </div>
          </div>
        </div>
      </template>

      <!-- ZPL panel -->
      <template v-else>
        <div class="edit-section-title">
          {{ $t('printingV2.sectionZpl') }}
        </div>

        <div class="edit-field-row">
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldDensity')
            }}</label>
            <PosUiListbox
              :value="dpmm"
              :items="dpmmItems"
              @input="dpmm = pickOption($event, DPMM_OPTIONS, 12)"
            />
          </div>
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldZplWidth')
            }}</label>
            <PosUiInput
              :value="zplWidthMm"
              type="number"
              inputmode="decimal"
              suffix="mm"
              @input="zplWidthMm = $event"
            />
            <p class="edit-hint">
              {{ $t('printingV2.zplDotsHint', { dots: zplWidthDots }) }}
            </p>
          </div>
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldZplHeight')
            }}</label>
            <PosUiInput
              :value="zplHeightMm"
              type="number"
              inputmode="decimal"
              suffix="mm"
              @input="zplHeightMm = $event"
            />
            <p class="edit-hint">
              {{ $t('printingV2.zplDotsHint', { dots: zplHeightDots }) }}
            </p>
          </div>
        </div>

        <div class="edit-field">
          <label class="edit-label">{{
            $t('printingV2.fieldZplMargins')
          }}</label>
          <div class="edit-field-row">
            <div class="edit-field">
              <PosUiInput
                :value="padTop"
                type="number"
                inputmode="numeric"
                :prefix="$t('printingV2.marginTop')"
                @input="padTop = $event"
              />
            </div>
            <div class="edit-field">
              <PosUiInput
                :value="padRight"
                type="number"
                inputmode="numeric"
                :prefix="$t('printingV2.marginRight')"
                @input="padRight = $event"
              />
            </div>
            <div class="edit-field">
              <PosUiInput
                :value="padBottom"
                type="number"
                inputmode="numeric"
                :prefix="$t('printingV2.marginBottom')"
                @input="padBottom = $event"
              />
            </div>
            <div class="edit-field">
              <PosUiInput
                :value="padLeft"
                type="number"
                inputmode="numeric"
                :prefix="$t('printingV2.marginLeft')"
                @input="padLeft = $event"
              />
            </div>
          </div>
          <p class="edit-hint">{{ $t('printingV2.zplMarginsHint') }}</p>
        </div>

        <div class="edit-field-row">
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldZplFontSize')
            }}</label>
            <PosUiListbox
              :value="zplFontSize"
              :items="fontSizeItems"
              @input="zplFontSize = pickOption($event, FONT_SIZE_OPTIONS, 'm')"
            />
          </div>
          <div class="edit-field">
            <label class="edit-label">{{ $t('printingV2.fieldOffsetX') }}</label>
            <PosUiInput
              :value="offsetXmm"
              type="number"
              inputmode="decimal"
              suffix="mm"
              @input="offsetXmm = $event"
            />
          </div>
          <div class="edit-field">
            <label class="edit-label">{{ $t('printingV2.fieldOffsetY') }}</label>
            <PosUiInput
              :value="offsetYmm"
              type="number"
              inputmode="decimal"
              suffix="mm"
              @input="offsetYmm = $event"
            />
          </div>
        </div>
        <p class="edit-hint">{{ $t('printingV2.zplOffsetHelp') }}</p>

        <div class="edit-test-row">
          <PosUiButton
            size="sm"
            color="neutral"
            variant="outline"
            icon="ruler-square"
            :loading="testingHardware"
            :disabled="!testTarget || saving"
            @click="testZplCalibration"
          >
            {{ $t('printingV2.printCalibration') }}
          </PosUiButton>
        </div>
      </template>

      <div class="edit-field">
        <label class="edit-label">{{
          $t('printingV2.fieldAssignments')
        }}</label>
        <V2NativeAssignmentPicker
          :value="selection"
          :self-name="printerName"
          @input="selection = $event"
        />
      </div>
    </div>

    <div class="edit-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="emit('close')"
      >
        {{ $t('printingV2.cancel') }}
      </PosUiButton>
      <PosUiButton
        color="primary"
        :loading="saving"
        :disabled="!canSave"
        @click="save"
      >
        {{ $t('printingV2.save') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-native-printer-edit-modal {
    @apply tw-flex tw-flex-col tw-bg-white;

    .edit-header {
      @apply tw-flex tw-items-center tw-justify-between tw-border-b tw-border-neutral-100 tw-px-5 tw-py-4;

      .edit-title {
        @apply tw-m-0 tw-text-base tw-font-bold tw-text-neutral-900;
      }

      .edit-close {
        @apply tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
      }
    }

    .edit-body {
      @apply tw-flex tw-flex-col tw-gap-4 tw-px-5 tw-py-4;

      .edit-section-title {
        @apply tw-mt-1 tw-border-t tw-border-neutral-100 tw-pt-3 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-400;
      }

      .edit-field {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5;

        .edit-label {
          @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
        }

        .edit-paper-toggle {
          @apply tw-flex tw-gap-0.5 tw-self-start tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

          .paper-option {
            @apply tw-rounded-md tw-border tw-border-transparent tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors hover:tw-text-neutral-800;

            &.active {
              @apply tw-border-neutral-200 tw-bg-white tw-font-semibold tw-text-neutral-900 tw-shadow-sm;
            }
          }
        }
      }

      .edit-field-row {
        @apply tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row;
      }

      .edit-hint {
        @apply tw-m-0 tw-text-xs tw-text-neutral-400;
      }

      .edit-test-row {
        @apply tw-flex tw-flex-wrap tw-gap-2 tw-pt-1;
      }

      .edit-collapse-toggle {
        @apply tw-flex tw-items-center tw-gap-1 tw-self-start tw-text-sm tw-font-medium tw-text-neutral-500 hover:tw-text-neutral-800;
      }

      .edit-advanced {
        @apply tw-rounded-lg tw-bg-neutral-50 tw-p-3;
      }

      .edit-address-row {
        @apply tw-flex tw-gap-3;

        .edit-field-host {
          @apply tw-min-w-0 tw-flex-1;
        }

        .edit-field-port {
          @apply tw-w-24 tw-flex-none;
        }
      }

      .edit-error {
        @apply tw-text-xs tw-font-medium tw-text-red-600;
      }

      .edit-note {
        @apply tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-neutral-50 tw-px-3 tw-py-2 tw-text-xs tw-text-neutral-500;

        .edit-note-icon {
          @apply tw-mt-0.5 tw-flex-none tw-text-base tw-text-neutral-400;
        }
      }

      .edit-identity {
        @apply tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-px-3 tw-py-2.5;

        .edit-identity-icon {
          @apply tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-white tw-text-neutral-500;
        }

        .edit-identity-info {
          @apply tw-min-w-0;

          .edit-identity-name {
            @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
          }

          .edit-identity-detail {
            @apply tw-truncate tw-text-xs tw-text-neutral-500;
          }
        }
      }
    }

    .edit-footer {
      @apply tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-100 tw-px-5 tw-py-4;
    }
  }
</style>
